import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Alert, Button, Input, Space, Table, Tag, Typography, message } from 'antd';
import { EnterprisePageHeader, WorkspaceLayout } from '@/components/enterprise';
import pmvService from '@/services/pmv.service';

const { Text, Paragraph } = Typography;
const { TextArea } = Input;

/**
 * Component bulk import — preview before commit.
 * Route: /asset/pmv/components/import
 */
const PmvComponentImportPage: React.FC = () => {
  const navigate = useNavigate();
  const [csv, setCsv] = useState('');
  const templateQ = useQuery({
    queryKey: ['pmv-component-import-template'],
    queryFn: () => pmvService.getComponentImportTemplate(),
  });
  const diagQ = useQuery({
    queryKey: ['pmv-component-diagnostics'],
    queryFn: () => pmvService.getComponentDiagnostics(),
    staleTime: 60_000,
  });

  const previewMut = useMutation({
    mutationFn: () => pmvService.previewComponentImport({ csv }),
    onError: (e: Error) => message.error(e.message || 'Preview failed'),
  });

  const commitMut = useMutation({
    mutationFn: () => pmvService.commitComponentImport({ csv }),
    onSuccess: (data) => {
      message.success(`Imported ${data.created} component(s)`);
      navigate('/asset/pmv/components');
    },
    onError: (e: Error) => message.error(e.message || 'Commit failed'),
  });

  const preview = previewMut.data;

  return (
    <WorkspaceLayout>
      <EnterprisePageHeader
        documentLabel="Component import"
        subtitle="CSV preview → commit. Opening rows (opening=1) keep PARTIAL confidence — purchase history is never fabricated."
        primaryActions={
          <Space>
            <Button onClick={() => navigate('/asset/pmv/components')}>Register</Button>
            <Button
              onClick={() => {
                if (templateQ.data?.sample_csv) setCsv(templateQ.data.sample_csv);
              }}
            >
              Load sample
            </Button>
          </Space>
        }
      />

      {(diagQ.data?.issue_count ?? 0) > 0 ? (
        <Alert
          style={{ marginBottom: 16 }}
          type="warning"
          showIcon
          message={`Component diagnostics: ${diagQ.data!.issue_count} issue(s)`}
          description="Read-only integrity scan — no auto-fix. Review before large imports."
        />
      ) : null}

      <Paragraph type="secondary">{templateQ.data?.note}</Paragraph>
      <TextArea
        rows={12}
        value={csv}
        onChange={(e) => setCsv(e.target.value)}
        aria-label="Component import CSV"
        placeholder="Paste CSV with header row…"
        style={{ fontFamily: 'monospace', marginBottom: 12, width: '100%' }}
      />
      <Space wrap style={{ marginBottom: 16 }}>
        <Button type="primary" loading={previewMut.isPending} onClick={() => previewMut.mutate()}>
          Preview
        </Button>
        <Button
          danger
          disabled={!preview?.can_commit}
          loading={commitMut.isPending}
          onClick={() => commitMut.mutate()}
        >
          Commit {preview?.ok_count ? `(${preview.ok_count})` : ''}
        </Button>
      </Space>

      {preview ? (
        <>
          <Alert
            style={{ marginBottom: 12 }}
            type={preview.can_commit ? 'success' : 'error'}
            showIcon
            message={
              preview.can_commit
                ? `${preview.ok_count} row(s) ready to commit`
                : `${preview.error_count} row error(s) — fix before commit`
            }
          />
          <Table<{
            row_number: number;
            ok: boolean;
            normalized: {
              family?: string;
              serial_number?: string | null;
              component_number?: string | null;
            };
            errors: Array<{ message: string }>;
            warnings: string[];
          }>
            rowKey="row_number"
            size="small"
            pagination={false}
            scroll={{ x: 720 }}
            dataSource={preview.rows}
            columns={[
              { title: '#', dataIndex: 'row_number', width: 50 },
              {
                title: 'OK',
                dataIndex: 'ok',
                width: 70,
                render: (ok) => (
                  <Tag color={ok ? 'success' : 'error'}>{ok ? 'OK' : 'ERR'}</Tag>
                ),
              },
              {
                title: 'Family',
                render: (_, r) => r.normalized.family || '—',
              },
              {
                title: 'Serial / Number',
                render: (_, r) =>
                  [r.normalized.component_number, r.normalized.serial_number]
                    .filter(Boolean)
                    .join(' · ') || '—',
              },
              {
                title: 'Errors',
                render: (_, r) => r.errors.map((e) => e.message).join('; ') || '—',
              },
              {
                title: 'Warnings',
                render: (_, r) => r.warnings.join('; ') || '—',
              },
            ]}
          />
        </>
      ) : null}

      {diagQ.data?.issues?.length ? (
        <div style={{ marginTop: 24 }}>
          <Text strong>Integrity diagnostics (no auto-fix)</Text>
          <Table
            style={{ marginTop: 8 }}
            rowKey={(r) => `${r.code}-${r.component_id || r.message}`}
            size="small"
            pagination={{ pageSize: 10, responsive: true }}
            scroll={{ x: 640 }}
            dataSource={diagQ.data.issues}
            columns={[
              { title: 'Code', dataIndex: 'code', width: 220 },
              { title: 'Component', dataIndex: 'component_id', width: 160, render: (v) => v || '—' },
              { title: 'Message', dataIndex: 'message' },
            ]}
          />
        </div>
      ) : null}
    </WorkspaceLayout>
  );
};

export default PmvComponentImportPage;
